import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";

interface BranchAssignmentDto {
  id: number;
  branchName: string;
  branchPrefix: string | null;
}

interface EmployeeBranchesDto {
  branches: BranchAssignmentDto[];
}

interface BranchListItem {
  id: number;
  branchName: string;
  branchPrefix: string | null;
}

interface EmployeeBranchesTabProps {
  employeeId: number;
  hasUserId: boolean;
}

export function EmployeeBranchesTab({ employeeId, hasUserId }: EmployeeBranchesTabProps) {
  const qc = useQueryClient();

  const all = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => apiFetch<{ items: BranchListItem[] }>("/api/branches?pageSize=200"),
    enabled: hasUserId,
  });
  const current = useQuery({
    queryKey: ["employee-branches", employeeId],
    queryFn: () => apiFetch<EmployeeBranchesDto>(`/api/employees/${employeeId}/branches`),
    enabled: hasUserId,
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (current.data) setSelected(new Set(current.data.branches.map((b) => b.id)));
  }, [current.data]);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/api/employees/${employeeId}/branches`, {
        method: "PUT",
        body: JSON.stringify({ branchIds: Array.from(selected) }),
      }),
    onSuccess: () => {
      toast.success("Branch assignments saved");
      void qc.invalidateQueries({ queryKey: ["employee-branches", employeeId] });
    },
    onError: () => toast.error("Failed to save branch assignments"),
  });

  if (!hasUserId) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        This employee has no Identity user. Branch assignment is unavailable.
      </p>
    );
  }
  if (all.isLoading || current.isLoading) {
    return <p className="py-4 text-sm text-muted-foreground">Loading…</p>;
  }
  if (all.isError || current.isError) {
    return (
      <p className="py-4 text-sm text-destructive">
        Failed to load branch data.
      </p>
    );
  }

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const branches = all.data?.items ?? [];

  return (
    <div className="space-y-4 py-2">
      <p className="text-sm text-muted-foreground">
        Select the branches this user can see Sales-scoped data for. Admin / Finance / SalesLead /
        ReadOnly users see all branches regardless of this list.
      </p>
      <ul className="space-y-2">
        {branches.map((b) => (
          <li key={b.id} className="flex items-center gap-2">
            <Checkbox
              id={`branch-${b.id}`}
              checked={selected.has(b.id)}
              onCheckedChange={() => toggle(b.id)}
              disabled={save.isPending}
            />
            <label htmlFor={`branch-${b.id}`} className="cursor-pointer">
              {b.branchName}
              {b.branchPrefix && (
                <span className="text-muted-foreground"> ({b.branchPrefix})</span>
              )}
            </label>
          </li>
        ))}
        {branches.length === 0 && (
          <li className="text-sm text-muted-foreground">No branches defined.</li>
        )}
      </ul>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}
